import { Tag, Button, Select } from "antd";
import { Link } from "react-router-dom";
import { PlayCircleOutlined } from "@ant-design/icons";
import StatusAvatar from "../../components/statusAvatar";
import { img } from "../../assets";

export interface TrainerOption { label: string; value: string; }

/* ─── Types ──────────────────────────────────────────────────────────── */

export interface CouponRecord {
  membershipId?:      string;
  couponId?:          string;
  sessionId?:         string;
  userId?:            string;
  userName?:          string;
  userPhone?:         string;
  profile?:           string;
  branchId?:          string;
  branchName?:        string;
  planName?:          string;
  serviceType?:       string;
  couponType?:        string;
  trainerId?:         string;
  trainerName?:       string;
  status?:            string;
  canEdit?:           boolean;
  startedAt?:         string;
  endedAt?:           string;
  totalSessions?:     number;
  usedSessions?:      number;
  remainingSessions?: number;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

const formatLabel = (v?: string) =>
  v ? v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-';

const statusColor = (status?: string) => {
  switch (status) {
    case 'pending':   return 'orange';
    case 'live':      return 'blue';
    case 'completed': return 'green';
    default:          return 'default';
  }
};

/* ─── Columns ────────────────────────────────────────────────────────── */

export const getCouponColumns = (
  onStart: (record: CouponRecord) => void,
  canStart: boolean,
  startingKey: string | null = null,
  onAssignTrainer?: (record: CouponRecord, coachId: string) => void,
  trainerOptions: TrainerOption[] = [],
  assigningKey: string | null = null,
  onUnassignTrainer?: (record: CouponRecord) => void,
) => [
  { title: 'Branch', dataIndex: 'branchName', key: 'branchName', width: 160, render: (v?: string) => v || '-' },
  {
    title:  'Profile',
    dataIndex: 'profile',
    key:    'profile',
    width:  80,
    render: (v?: string) => <StatusAvatar src={v || img} />,
  },
  {
    title:  'Name',
    dataIndex: 'userName',
    key:    'name',
    width:  160,
    render: (text: string, record: CouponRecord) =>
      record.userId ? (
        <Link to={`/user-detail/${record.userId}/membership`} style={{ color: 'inherit', textDecoration: 'none' }}>
          {text || '-'}
        </Link>
      ) : (text || '-'),
  },
  { title: 'Phone No.',  dataIndex: 'userPhone', key: 'phoneNumber', width: 140, render: (v?: string) => v || '-' },
  {
    title:  'Service Type',
    dataIndex: 'serviceType',
    key:    'serviceType',
    width:  150,
    render: (v?: string) => <Tag color="purple">{formatLabel(v)}</Tag>,
  },
  {
    title:  'Type',
    dataIndex: 'couponType',
    key:    'couponType',
    width:  110,
    render: (v?: string) => <Tag color="geekblue">{formatLabel(v)}</Tag>,
  },
  {
    title:  'Trainer Name',
    dataIndex: 'trainerName',
    key:    'trainerName',
    width:  180,
    render: (v?: string, record?: CouponRecord) => {
      if (!record) return v || '-';
      const options = record.trainerId && record.trainerName && !trainerOptions.some(o => o.value === record.trainerId)
        ? [{ label: record.trainerName, value: record.trainerId }, ...trainerOptions]
        : trainerOptions;
      return (
        <Select
          placeholder="Select trainer"
          style={{ width: '100%' }}
          value={record.trainerId ?? undefined}
          allowClear
          showSearch
          disabled
          options={options}
        />
      );
    },
  },
  {
    title:  'Status',
    dataIndex: 'status',
    key:    'status',
    width:  110,
    align:  'center' as const,
    render: (v?: string) => <Tag color={statusColor(v)} style={{ textTransform: 'capitalize' }}>{v || '-'}</Tag>,
  },
  ...(canStart ? [{
    title:  'Actions',
    key:    'actions',
    width:  120,
    align:  'center' as const,
    render: (_: unknown, record: CouponRecord) => {
      if (record.status !== 'pending') return null;
      const key = record.sessionId ?? `${record.membershipId}-${record.serviceType}`;
      return (
        <Button
          type="primary"
          size="small"
          icon={<PlayCircleOutlined />}
          loading={startingKey === key}
          onClick={() => onStart(record)}
        >
          Start
        </Button>
      );
    },
  }] : []),
];
